import React, { useContext, useEffect, useState, Suspense } from 'react'
import './Tailwind.css'
import { getCompanyInfoHelper } from './helpers/getCompanyInfoHelper'
import { DataContext } from './context/DataContext'
import { CheckoutSkeleton, Spinner } from '@pinflag/pinflag-ui-kit'
import { decryptToken, getPinflagApiKey } from './helpers/utils'
import {
  isLocalStorageAvailable,
  setCompanyLocalStorage
} from './helpers/localStorage'

const MagentoProviderLazy = React.lazy(() =>
  import('./context/MagentoContext').then((module) => ({
    default: module.MagentoProvider
  }))
)
const PinmapProLazy = React.lazy(() => import('./PinmapPro'))

const App = () => {
  const { setUserInfo, setCompanyInfo, isPickup, companyInfo } =
    useContext(DataContext)
  const [app, setApp] = useState(<CheckoutSkeleton isPickup={isPickup} />)
  const isLoaderIframe = new URLSearchParams(document.location.search).get(
    'loaderIframe'
  )
  const SpinnerComponent = (
    <div className="full_width_spinner">
      <Spinner />
    </div>
  )

  useEffect(() => {
    window.addEventListener('message', receiveMessage)
    getCompanyInfoHelper(setUserInfo, setCompanyInfo)
    return () => {
      window.removeEventListener('message', receiveMessage)
    }
  }, [])

  useEffect(() => {
    if (isLoaderIframe) {
      setApp(
        <Suspense fallback={SpinnerComponent}>
          <MagentoProviderLazy>
            <PinmapProLazy />
          </MagentoProviderLazy>
        </Suspense>
      )
      window.removeEventListener('message', receiveMessage)
    }
  }, [isLoaderIframe])

  const processCompanyInfo = (companyData) => {
    const processedCompanyData = {
      ...companyData,
      mapboxToken:
        decryptToken(
          companyData.mapboxToken,
          process.env.REACT_APP_TOKEN_ENCRYPTATION_KEY
        ) || process.env.REACT_APP_MAPBOX_TOKEN
    }

    if (isLocalStorageAvailable()) {
      const pinflagApiKey = getPinflagApiKey()
      if (pinflagApiKey) {
        setCompanyLocalStorage({
          ...processedCompanyData,
          pinflagApiKey
        })
      }
    }

    setCompanyInfo((prevState) => ({
      ...prevState,
      ...processedCompanyData
    }))
  }

  const receiveMessage = async (event) => {
    if (event?.data?.type) {
      const { message, origin, type, pinflagApiKey } = event.data

      if (type === 'request-company-info') {
        const currentPinflagApiKey =
          new URLSearchParams(document.location.search).get('token') ||
          process.env.REACT_APP_API_KEY
        if (
          pinflagApiKey === currentPinflagApiKey &&
          companyInfo?.companyName
        ) {
          window.parent.postMessage(
            {
              type: 'response-company-info',
              origin: 'pinflag-shopify-pinmap-pro',
              pinflagApiKey: currentPinflagApiKey,
              message: companyInfo
            },
            '*'
          )
        }
      } else if (type === 'receive-company-info') {
        processCompanyInfo(message)
      } else if (type === 'receive-cart-info') {
        if (message?.companyInfo) {
          processCompanyInfo(message.companyInfo)
        } else {
          getCompanyInfoHelper(setUserInfo, setCompanyInfo)
        }
        const pinmap = import('./PinmapPro')
        const magento = import('./context/MagentoContext')
        const [{ default: PinmapPro }, { MagentoProvider }] = await Promise.all(
          [pinmap, magento]
        )
        setApp(
          <MagentoProvider>
            <PinmapPro cartMessage={message} cartOrigin={origin} />
          </MagentoProvider>
        )
        window.removeEventListener('message', receiveMessage)
      } else if (type === 'is-ready') {
        if (!companyInfo?.companyName) {
          getCompanyInfoHelper(setUserInfo, setCompanyInfo)
        }
      }
    }
  }

  return app
}

export default App
